import React, { useEffect, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { supabase } from './services/supabaseClient';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import OnboardingGuard from './components/OnboardingGuard';
import FeatureRoute from './components/FeatureRoute';
import Chat from './components/Chat';
import MainLayout from './components/layout/MainLayout';
import { AnimationProvider } from './context/AnimationContext';
import { CustomThemeProvider } from './context/ThemeContext';
import { ChatStateProvider } from './context/ChatStateContext';
import { AuthProvider } from './context/AuthContext';
import { ConnectionProvider } from './context/ConnectionContext';
import { ProviderStatusProvider } from './context/ProviderStatusContext';
import { SidebarProvider } from './context/SidebarContext';
import { ThreadProvider } from './context/ThreadContext';
import { RagJobProvider } from './context/RagJobContext';
import { AgentPreferencesProvider } from './context/AgentPreferencesContext';
import { OrgProvider } from './context/OrgContext';
import { BillingProvider } from './context/BillingContext';
import { ConnectionBanner } from './components/connection';
import ProviderStatusBanner from './components/connection/ProviderStatusBanner';
import TermsRedirectListener from './components/common/TermsRedirectListener';
import AccountStatusListener from './components/common/AccountStatusListener';
import TrialExpiredListener from './components/common/TrialExpiredListener';
import RenewalListener from './components/common/RenewalListener';
import UsageWarningBanner from './components/billing/UsageWarningBanner';
import RenewalGraceNotice from './components/billing/RenewalGraceNotice';
import UsageLimitModal from './components/billing/UsageLimitModal';
import CreditStopNotice from './components/billing/CreditStopNotice';
import GlobalRagProgressPanel from './components/rag/GlobalRagProgressPanel';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingScreen from './components/common/LoadingScreen';
import { initErrorBuffer } from './utils/errorBuffer';

// Lazy-loaded routes (code-split into separate chunks)
const Login = React.lazy(() => import('./components/Login'));
const Register = React.lazy(() => import('./components/Register'));
const EditorPage = React.lazy(() => import('./components/editor/EditorPage'));
const KnowledgeManagerPage = React.lazy(() => import('./pages/KnowledgeManagerPage'));
const RoleBuilderPage = React.lazy(() => import('./components/editor/features/roles/RoleBuilderPage'));
const ExecutionsPage = React.lazy(() => import('./components/editor/features/plans/ExecutionsPage'));
const CollectionsOverviewPage = React.lazy(() => import('./pages/CollectionsOverviewPage'));
const PresasPage = React.lazy(() => import('./pages/PresasPage'));
const ExploreAgentsPage = React.lazy(() => import('./pages/ExploreAgentsPage'));
const MyReportsPage = React.lazy(() => import('./pages/MyReportsPage'));
const SettingsPage = React.lazy(() => import('./components/settings/SettingsPage'));
const ConnectAppsPage = React.lazy(() => import('./components/onboarding/ConnectAppsPage'));
const OnboardingChatPage = React.lazy(() => import('./pages/OnboardingChatPage'));
const TermsConsentPage = React.lazy(() => import('./pages/TermsConsentPage'));
const LegalDocPage = React.lazy(() => import('./pages/LegalDocPage'));
const FreemiumSignupPage = React.lazy(() => import('./pages/FreemiumSignupPage'));
const StatusPage = React.lazy(() => import('./pages/StatusPage'));
const JoinGroupPage = React.lazy(() => import('./components/join/JoinGroupPage'));
const AdminUsersPage = React.lazy(() => import('./pages/AdminUsersPage'));
const AdminUserDetailPage = React.lazy(() => import('./pages/AdminUserDetailPage'));
const AdminUserUsagePage = React.lazy(() => import('./pages/AdminUserUsagePage'));
const AdminOrgUsagePage = React.lazy(() => import('./pages/AdminOrgUsagePage'));
const AdminHealthPage = React.lazy(() => import('./pages/AdminHealthPage'));
const AdminOrgsPage = React.lazy(() => import('./pages/AdminOrgsPage'));
const AdminWaitlistPage = React.lazy(() => import('./pages/AdminWaitlistPage'));
const AdminCompCodesPage = React.lazy(() => import('./pages/AdminCompCodesPage'));
const AdminOrphanPaymentsPage = React.lazy(() => import('./pages/AdminOrphanPaymentsPage'));
const AdminPartnersPage = React.lazy(() => import('./pages/AdminPartnersPage'));
const AdminFinancePage = React.lazy(() => import('./pages/AdminFinancePage'));
const BillingRequiredPage = React.lazy(() => import('./pages/BillingRequiredPage'));
const BillingRenewalPage = React.lazy(() => import('./pages/BillingRenewalPage'));
const AccountInactivePage = React.lazy(() => import('./pages/AccountInactivePage'));
const AdminReportsPage = React.lazy(() => import('./components/admin/AdminReportsPage'));
const AdminCostsPage = React.lazy(() => import('./components/admin/AdminCostsPage'));
const AdminToolUsagePage = React.lazy(() => import('./components/admin/AdminToolUsagePage'));
const AdminFeedbackPage = React.lazy(() => import('./components/admin/AdminFeedbackPage'));
const AdminLayout = React.lazy(() => import('./components/admin/AdminLayout'));
const AnalyticsPage = React.lazy(() => import('./pages/AnalyticsPage'));

// ✅ CRITICAL: Configure React Query client for message reconciliation
// This replaces the custom message state management with battle-tested solution
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,        // 30s - data stays fresh (don't refetch immediately)
      refetchInterval: 60000,  // 60s - background sync every minute
      retry: 3,                 // Auto-retry failed queries 3 times
      refetchOnWindowFocus: false, // ✅ Disabled: Prevents race conditions with WebSocket messages (see useMessages.ts:117-130)
      // N8N can take a LONG time to respond, so be patient
      refetchOnReconnect: false // ✅ Disabled: Prevents race conditions with WebSocket messages (see useMessages.ts:117-130)
    },
    mutations: {
      retry: 1 // Retry mutations once if they fail
    }
  }
});

function App() {
  // Initialize error buffer for report context capture
  useEffect(() => {
    initErrorBuffer();
  }, []);

  // 🔐 SECURITY: Track previous user ID to detect actual user changes
  // This prevents clearing cache on SIGNED_IN events that are just session refreshes
  const previousUserIdRef = useRef(null);

  // 🔐 SECURITY: Clear React Query cache on logout to prevent data leakage between users
  // This complements the state clearing in useThreadManagement and ChatStateContext
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        console.log('[App] 🔐 Clearing React Query cache on logout');
        // Clear ALL cached data to prevent cross-user data leakage
        queryClient.clear();
        previousUserIdRef.current = null;
      }

      // Only clear cache when a DIFFERENT user signs in (not on session refresh)
      // Supabase emits SIGNED_IN on tab visibility change for session verification,
      // which was incorrectly clearing the message cache
      if (event === 'SIGNED_IN' && session?.user?.id) {
        const currentUserId = session.user.id;
        const previousUserId = previousUserIdRef.current;

        // Only clear if this is actually a different user
        if (previousUserId && previousUserId !== currentUserId) {
          console.log('[App] 🔐 Different user signed in, clearing previous cache');
          queryClient.clear();
        } else if (!previousUserId) {
          console.log('[App] 🔐 First sign in detected, no cache to clear');
        } else {
          console.log('[App] 🔐 Same user session refresh, preserving cache');
        }

        previousUserIdRef.current = currentUserId;
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <CustomThemeProvider>
        <ConnectionProvider>
          <ProviderStatusProvider>
          <AuthProvider>
            <AnimationProvider>
              <ChatStateProvider>
                <Router future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true
                }}>
                  <SidebarProvider>
                    <ThreadProvider>
                      <AgentPreferencesProvider>
                      <OrgProvider>
                      <RagJobProvider>
                      <BillingProvider>
                      <ConnectionBanner />
                      <ProviderStatusBanner />
                      <TermsRedirectListener />
                      <AccountStatusListener />
                      <TrialExpiredListener />
                      <RenewalListener />
                      <UsageWarningBanner />
                      <RenewalGraceNotice />
                      <UsageLimitModal />
                      <CreditStopNotice />
                      <GlobalRagProgressPanel />
                      <MainLayout>
                    <ErrorBoundary>
                    <Suspense fallback={<LoadingScreen />}>
                    <Routes>
                    {/* Rutas públicas */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/legal/:docSlug" element={<LegalDocPage />} />
                    {/* #1298: alta freemium pública, enlazada desde la web */}
                    <Route path="/registro-freemium" element={<FreemiumSignupPage />} />
                    {/* #1722: estado público del proveedor LLM */}
                    <Route path="/status" element={<StatusPage />} />

                  {/* Rutas de Onboarding (Protegidas) */}
                  {/* Ruta /onboarding/setup-group removida - Work groups ahora se gestionan solo desde Settings */}
                  {/*
                    MVP: Template selection route hidden for initial release.
                    Users get templates assigned via registration codes only.
                    Uncomment to re-enable template selection during onboarding.
                    See: /tmpo/templates-implementation-state.md
                  */}
                  {/* <Route
                    path="/onboarding/template"
                    element={
                      <PrivateRoute>
                        <SelectTemplatePage />
                      </PrivateRoute>
                    }
                  /> */}
                  {/* MVP: Redirect /onboarding/template to /onboarding/connect */}
                  <Route
                    path="/onboarding/template"
                    element={<Navigate to="/onboarding/connect" replace />}
                  />
                  <Route
                    path="/onboarding/terms"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <TermsConsentPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/onboarding/connect"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <ConnectAppsPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/onboarding/chat"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <OnboardingChatPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />

                  {/* Rutas protegidas - Requieren autenticación Y onboarding completado */}
                  <Route
                    path="/"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <Chat />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  {/* Editor routes with sidebar collapse */}
                  <Route
                    path="/editor"
                    element={<Navigate to="/editor/plans" replace />}
                  />
                  {/* Agent builder routes */}
                  <Route
                    path="/agents/builder"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <RoleBuilderPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/agents/builder/:rolId"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <RoleBuilderPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/editor/plans"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <EditorPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/executions"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <ExecutionsPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/plans/:planId/executions"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <ExecutionsPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/collections"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <CollectionsOverviewPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/presas"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <FeatureRoute feature="presas">
                            <PresasPage />
                          </FeatureRoute>
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/explore"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <ExploreAgentsPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/my-reports"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <MyReportsPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/editor/knowledge/:rolId"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <KnowledgeManagerPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <SettingsPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/analytics"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <AnalyticsPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/join-group"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <JoinGroupPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />

                  {/* Billing gate — shown when no active subscription.
                      Wrapped in OnboardingGuard so the terms-gate (step 0)
                      still fires here; the guard knows to bypass its own
                      billing check on /billing/* routes (no loop). */}
                  <Route
                    path="/billing/required"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <BillingRequiredPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />

                  {/* #759 R: la renovación de Stripe no se ha cobrado. Mismo envoltorio que
                      /billing/required: el guard no hace su propio chequeo en /billing/*. */}
                  <Route
                    path="/billing/renewal"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <BillingRenewalPage />
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  />

                  {/* #559 — cuenta suspendida/bloqueada.
                      DELIBERADAMENTE FUERA de OnboardingGuard: una cuenta
                      inactiva no debe ser redirigida al gate de terms ni al de
                      billing antes de poder leer por qué está bloqueada. El
                      backend es quien manda (403 + code); esto es solo la
                      pantalla explicativa. */}
                  <Route
                    path="/account/inactive"
                    element={
                      <PrivateRoute>
                        <AccountInactivePage />
                      </PrivateRoute>
                    }
                  />

                  {/* Admin routes — backend enforces requireAdmin; frontend guard is UX only.
                      Grouped under AdminLayout (dedicated sidebar). MainLayout skips AppShell
                      for /admin/* so the regular UnifiedSidebar doesn't render here. */}
                  <Route
                    path="/admin"
                    element={
                      <PrivateRoute>
                        <OnboardingGuard>
                          <AdminRoute>
                            <AdminLayout />
                          </AdminRoute>
                        </OnboardingGuard>
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<Navigate to="/admin/orgs" replace />} />
                    {/* #557. `users/:id` va DESPUÉS de `users`: el router de v6 elige por
                        especificidad y no por orden, pero mantenerlo legible importa más que
                        apurar la regla. `health` es un segmento literal y no compite con
                        ningún `:id`. */}
                    <Route path="users" element={<AdminUsersPage />} />
                    <Route path="users/:id" element={<AdminUserDetailPage />} />
                    <Route path="users/:id/usage" element={<AdminUserUsagePage />} />
                    <Route path="health" element={<AdminHealthPage />} />
                    <Route path="orgs" element={<AdminOrgsPage />} />
                    <Route path="orgs/:orgId/usage" element={<AdminOrgUsagePage />} />
                    <Route path="waitlist" element={<AdminWaitlistPage />} />
                    <Route path="comp-codes" element={<AdminCompCodesPage />} />
                    {/* PRP invite-model-3-paths-canonical D5: redirect 30 días para bookmarks legacy.
                        Eliminar este redirect en 2026-06-26 (30 días tras merge a main). */}
                    <Route path="invites" element={<Navigate to="/admin/waitlist" replace />} />
                    <Route path="orphan-payments" element={<AdminOrphanPaymentsPage />} />
                    <Route path="partners" element={<AdminPartnersPage />} />
                    <Route path="finanzas" element={<AdminFinancePage />} />
                    <Route path="reports" element={<AdminReportsPage />} />
                    <Route path="costs" element={<AdminCostsPage />} />
                    <Route path="tool-usage" element={<AdminToolUsagePage />} />
                    <Route path="feedback" element={<AdminFeedbackPage />} />
                  </Route>

                  {/* Redirigir rutas no encontradas al chat */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                    </Suspense>
                    </ErrorBoundary>
                      </MainLayout>
                      </BillingProvider>
                      </RagJobProvider>
                      </OrgProvider>
                      </AgentPreferencesProvider>
                    </ThreadProvider>
                  </SidebarProvider>
                </Router>
            </ChatStateProvider>
          </AnimationProvider>
        </AuthProvider>
          </ProviderStatusProvider>
      </ConnectionProvider>
      </CustomThemeProvider>

    </QueryClientProvider>
  );
}

export default App; 